import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar } from 'lucide-react';
import { getBlogArticles } from '../lib/api';

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const data = await getBlogArticles();
        setArticles(data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-ghrb-orange border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="blog-page" className="min-h-screen bg-white">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-ghrb-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-2xl"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-ghrb-orange mb-4">
              Journal GHRB
            </p>
            <h1 className="font-outfit text-4xl lg:text-5xl font-bold tracking-tight text-ghrb-black mb-6">
              Conseils & Astuces
            </h1>
            <p className="font-jakarta text-lg text-ghrb-gray-dark">
              Découvrez nos articles pour un quotidien plus simple et efficace. 
              Des conseils pratiques testés et approuvés.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {articles.length > 0 ? (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              {articles.map((article) => (
                <motion.article key={article.id} variants={fadeInUp}>
                  <Link 
                    to={`/journal/${article.slug}`}
                    className="block group"
                    data-testid={`blog-card-${article.slug}`}
                  >
                    <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-ghrb-gray mb-6">
                      <img
                        src={article.cover_image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-outfit text-xs uppercase tracking-[0.15em] text-ghrb-orange">
                        {article.category}
                      </span>
                      <span className="w-1 h-1 bg-ghrb-gray-dark rounded-full"></span>
                      <span className="font-jakarta text-xs text-ghrb-gray-dark flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.created_at)}
                      </span>
                    </div>
                    
                    <h2 className="font-outfit text-xl font-semibold text-ghrb-black mb-3 group-hover:text-ghrb-orange transition-colors">
                      {article.title}
                    </h2>
                    
                    <p className="font-jakarta text-ghrb-gray-dark text-sm line-clamp-3 mb-4">
                      {article.excerpt}
                    </p>
                    
                    <span className="inline-flex items-center font-jakarta text-sm font-medium text-ghrb-orange">
                      Lire l'article
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.article>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-16">
              <p className="font-jakarta text-ghrb-gray-dark mb-4">
                Aucun article disponible pour le moment.
              </p>
              <p className="font-jakarta text-sm text-ghrb-gray-dark">
                Revenez bientôt pour découvrir nos conseils et astuces !
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
